import { Injectable, inject, signal, effect, DestroyRef } from '@angular/core';

import { ViewerCoreService } from './viewer-core.service.js';
import { SceneStateService } from './scene-state.service';
import { CameraMode } from '../shared/defines.js';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private core = inject(ViewerCoreService);
  private sceneState = inject(SceneStateService);
  private destroyRef = inject(DestroyRef);

  // ─── Reactive state ─────────────────────────────────────────────────────────

  readonly cameraMode = signal<string>('PERSPECTIVE');
  readonly navigationMode = signal<string>('orbit');

  private _syncing = false;

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer) return;
      this._syncFromViewer(viewer);
    });

    effect(() => {
      const mode = this.cameraMode();
      this._pushToViewer(v => {
        const value = (CameraMode as any)[mode];
        if (value !== undefined) v.setCameraMode?.(value);
      });
    });

    effect(() => {
      const mode = this.navigationMode();
      this._pushToViewer(v => {
        switch (mode) {
          case 'orbit':
            if (v.orbitControls) v.setControls(v.orbitControls);
            break;
          case 'firstPerson':
            if (v.fpControls) v.setControls(v.fpControls);
            break;
        }
      });
    });
  }

  // ─── Static options ─────────────────────────────────────────────────────────

  getCameraModeOptions(): { value: string; label: string }[] {
    return [
      { value: 'PERSPECTIVE', label: 'Perspective' },
      { value: 'ORTHOGRAPHIC', label: 'Orthographic' },
    ];
  }

  getNavigationModeOptions(): { value: string; label: string }[] {
    return [
      { value: 'orbit', label: 'Orbit' },
      { value: 'firstPerson', label: 'First Person' },
    ];
  }

  // ─── Actions (imperative, no state) ─────────────────────────────────────────

  fitToScreen(): void {
    const viewer = this.core.viewer();
    if (viewer && typeof viewer.fitToScreen === 'function') {
      viewer.fitToScreen();
    }
  }

  setTopView(): void {
    const viewer = this.core.viewer();
    viewer?.setTopView?.();
  }

  setBottomView(): void {
    const viewer = this.core.viewer();
    viewer?.setBottomView?.();
  }

  setFrontView(): void {
    const viewer = this.core.viewer();
    viewer?.setFrontView?.();
  }

  setBackView(): void {
    const viewer = this.core.viewer();
    viewer?.setBackView?.();
  }

  setLeftView(): void {
    const viewer = this.core.viewer();
    viewer?.setLeftView?.();
  }

  setRightView(): void {
    const viewer = this.core.viewer();
    viewer?.setRightView?.();
  }

  zoomIn(): void {
    const viewer = this.core.viewer();
    if (!viewer) return;

    const view = this.sceneState.view();
    if (view && viewer.orbitControls) {
      const currentRadius = view.radius;
      const zoomStep = currentRadius * 0.15;
      viewer.orbitControls.radiusDelta -= zoomStep;
      if (currentRadius - zoomStep < 0.01) {
        viewer.orbitControls.radiusDelta = -(currentRadius - 0.01);
      }
    }
  }

  zoomOut(): void {
    const viewer = this.core.viewer();
    if (!viewer) return;

    const view = this.sceneState.view();
    if (view && viewer.orbitControls) {
      const currentRadius = view.radius;
      const zoomStep = currentRadius * 0.15;
      viewer.orbitControls.radiusDelta += zoomStep;
    }
  }

  tiltUp(): void {
    const view = this.sceneState.view();
    if (!view) return;

    const tiltStep = (10 * Math.PI) / 180;
    view.pitch += tiltStep;
    const maxPitch = Math.PI / 2 - 0.01;
    view.pitch = Math.min(view.pitch, maxPitch);
  }

  tiltDown(): void {
    const view = this.sceneState.view();
    if (!view) return;

    const tiltStep = (10 * Math.PI) / 180;
    view.pitch -= tiltStep;
    const minPitch = -Math.PI / 2 + 0.01;
    view.pitch = Math.max(view.pitch, minPitch);
  }

  rotateClockwise(): void {
    const view = this.sceneState.view();
    if (!view) return;

    const rotateStep = (10 * Math.PI) / 180;
    view.yaw -= rotateStep;
  }

  rotateCounterClockwise(): void {
    const view = this.sceneState.view();
    if (!view) return;

    const rotateStep = (10 * Math.PI) / 180;
    view.yaw += rotateStep;
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private _syncFromViewer(viewer: any): void {
    this._syncing = true;
    // Camera mode
    const currentMode = this.sceneState.cameraMode();
    for (const [key, value] of Object.entries(CameraMode)) {
      if (value === currentMode) {
        this.cameraMode.set(key);
        break;
      }
    }
    // Navigation mode
    if (viewer.controls === viewer.orbitControls) this.navigationMode.set('orbit');
    else if (viewer.controls === viewer.fpControls) this.navigationMode.set('firstPerson');
    this._syncing = false;
  }

  private _pushToViewer(fn: (viewer: any) => void): void {
    if (this._syncing) return;
    const viewer = this.core.viewer();
    if (viewer) fn(viewer);
  }
}
